import React, { useEffect, useMemo } from 'react'
import { NavBar } from 'components/NavBar/NavBar'
import { clearClientRole, getSingleClientRole } from 'features/roles/clientRolesSlice'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Flex,
  H1WithBack,
  PageWrapper,
  Separator,
  Box,
} from 'styling/GlobalStyles'
import { AppDispatch, RootState } from 'store'
import { BodyText, TitleText, OtherText } from 'styling/Typography'
import { days, shiftChangeTypes, logisticsTransportationTypes } from 'pages/Employees/employeesData'
import { getShiftFromMinutes } from 'utils/helpers'
import { ISingleClientRoleGetResponse } from 'types/api/clientRoles.interface'

export const SingleRole = () => {
  const navigate = useNavigate()
  const { singleRole } = useSelector((store: RootState) => store.clientRoles)
  const { id } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  const roleData = singleRole as unknown as ISingleClientRoleGetResponse

  useEffect(() => {
    dispatch(getSingleClientRole(id))

    return () => {
      dispatch(clearClientRole())
    }
  }, [dispatch, id])

  const shiftChangeTypeLabel = useMemo(() => {
    if (!roleData?.shiftChangeType) return null
    const type = shiftChangeTypes.find(t => t.value === roleData.shiftChangeType)
    return type?.label || 'N/A'
  }, [roleData?.shiftChangeType])

  const transportationTypeLabel = useMemo(() => {
    if (!roleData?.logisticsInfo?.transportationType) return null
    const type = logisticsTransportationTypes.find(t => t.value === roleData.logisticsInfo?.transportationType)
    return type?.label || 'N/A'
  }, [roleData?.logisticsInfo?.transportationType])

  const freeDaysLabels = useMemo(() => {
    if (!roleData?.freeDays) return []
    const freeDaysArray = Array.isArray(roleData.freeDays)
      ? roleData.freeDays
      : [roleData.freeDays]
    return freeDaysArray
      .map(day => days.find(d => d.value === day)?.label)
      .filter(Boolean) as string[]
  }, [roleData?.freeDays])

  const activePermissions = useMemo(() => {
    if (!roleData?.parmissions) return []
    return Object.entries(roleData.parmissions)
      .filter(([, value]) => value === true)
      .map(([key]) => key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'))
  }, [roleData?.parmissions])

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null) return 'N/A'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex between>
          <H1WithBack title={roleData?.name || 'Role Details'} arrow />
          <Button orange onClick={() => navigate('edit')}>
            Edit Role
          </Button>
        </Flex>
        <Separator />

        <Flex column gap='2rem' style={{ marginTop: '2rem' }}>
          <Box padding='1.5rem'>
            <TitleText weight='bold' style={{ marginBottom: '1rem' }}>Work Schedule</TitleText>
            <Flex column gap='1rem'>
              {roleData?.workScheduleModel ? (
                <>
                  <Flex column gap='0.5rem'>
                    <OtherText color='#888'>Work Schedule Model</OtherText>
                    <BodyText weight='500'>{roleData.workScheduleModel.name}</BodyText>
                  </Flex>
                  <Flex column gap='0.5rem'>
                    <OtherText color='#888'>Work Days Count</OtherText>
                    <BodyText weight='500'>{roleData.workScheduleModel.workDaysCount ?? 'N/A'}</BodyText>
                  </Flex>
                  <Flex column gap='0.5rem'>
                    <OtherText color='#888'>Rest Days Count</OtherText>
                    <BodyText weight='500'>{roleData.workScheduleModel.restDaysCount ?? 'N/A'}</BodyText>
                  </Flex>
                  <Flex column gap='0.5rem'>
                    <OtherText color='#888'>Is Week Model</OtherText>
                    <BodyText weight='500'>{roleData.workScheduleModel.isWeekModel ? 'Yes' : 'No'}</BodyText>
                  </Flex>
                </>
              ) : (
                <BodyText>No work schedule model configured</BodyText>
              )}
              <Flex column gap='0.5rem'>
                <OtherText color='#888'>Free Days</OtherText>
                <BodyText weight='500'>
                  {freeDaysLabels.length > 0 ? freeDaysLabels.join(', ') : 'None'}
                </BodyText>
              </Flex>
              <Flex column gap='0.5rem'>
                <OtherText color='#888'>Vacation Days</OtherText>
                <BodyText weight='500'>{roleData?.vacationDays ?? 0}</BodyText>
              </Flex>
            </Flex>
          </Box>

          <Box padding='1.5rem'>
            <TitleText weight='bold' style={{ marginBottom: '1rem' }}>Shift Configuration</TitleText>
            <Flex column gap='1rem'>
              <Flex column gap='0.5rem'>
                <OtherText color='#888'>Shift Change Type</OtherText>
                <BodyText weight='500'>{shiftChangeTypeLabel || 'N/A'}</BodyText>
              </Flex>
              <Flex column gap='0.5rem'>
                <OtherText color='#888'>Shift Change Interval</OtherText>
                <BodyText weight='500'>{roleData?.shiftChangeInterval ?? 'N/A'}</BodyText>
              </Flex>
              {roleData?.shifts && roleData.shifts.length > 0 ? (
                <Flex column gap='0.5rem'>
                  <OtherText color='#888'>Shifts</OtherText>
                  <Flex column gap='0.75rem' style={{ marginTop: '0.5rem' }}>
                    {roleData.shifts
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((shiftItem, index) => (
                        <Box key={shiftItem.id || index} padding='1rem' style={{ backgroundColor: 'rgba(0,0,0,0.05)' }}>
                          <Flex column gap='0.5rem'>
                            <BodyText weight='500'>{shiftItem.shift?.name || 'Unnamed Shift'}</BodyText>
                            {shiftItem.shift && (
                              <>
                                {shiftItem.shift.shiftStart !== undefined && shiftItem.shift.minutes !== undefined && (
                                  <OtherText>
                                    Time: {getShiftFromMinutes({
                                      shiftStart: shiftItem.shift.shiftStart,
                                      minutes: shiftItem.shift.minutes
                                    })}
                                  </OtherText>
                                )}
                                <OtherText>Order: {shiftItem.order ?? 'N/A'}</OtherText>
                                {shiftItem.shift.info && (
                                  <OtherText>Info: {shiftItem.shift.info}</OtherText>
                                )}
                                {shiftItem.shift.color && (
                                  <Flex gap='0.5rem' style={{ alignItems: 'center' }}>
                                    <OtherText>Color:</OtherText>
                                    <div
                                      style={{
                                        width: '20px',
                                        height: '20px',
                                        backgroundColor: shiftItem.shift.color,
                                        borderRadius: '4px',
                                        border: '1px solid #ddd',
                                      }}
                                    />
                                  </Flex>
                                )}
                              </>
                            )}
                          </Flex>
                        </Box>
                      ))}
                  </Flex>
                </Flex>
              ) : (
                <BodyText>No shifts configured</BodyText>
              )}
            </Flex>
          </Box>

          <Box padding='1.5rem'>
            <TitleText weight='bold' style={{ marginBottom: '1rem' }}>Permissions</TitleText>
            {activePermissions.length > 0 ? (
              <Flex column gap='0.75rem'>
                {activePermissions.map((permission, index) => (
                  <Flex key={index} gap='0.5rem' style={{ alignItems: 'center' }}>
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#4CAF50',
                      }}
                    />
                    <BodyText>{permission}</BodyText>
                  </Flex>
                ))}
              </Flex>
            ) : (
              <BodyText>No permissions assigned</BodyText>
            )}
          </Box>
          {/* logisticsInfo Information */}
          {roleData?.logisticsInfo && (
            <Box padding='1.5rem'>
              <TitleText weight='bold' style={{ marginBottom: '1rem' }}>logisticsInfo Information</TitleText>
              <Flex column gap='1.5rem'>
                <Flex column gap='1rem'>
                  <TitleText weight='600' style={{ fontSize: '14px' }}>Food</TitleText>
                  <Flex column gap='0.75rem' style={{ marginLeft: '1rem' }}>
                    <Flex column gap='0.5rem'>
                      <OtherText color='#888'>Food Provided</OtherText>
                      <BodyText weight='500'>{roleData.logisticsInfo.hasFoodProvided ? 'Yes' : 'No'}</BodyText>
                    </Flex>
                    <Flex column gap='0.5rem'>
                      <OtherText color='#888'>Food Allowance</OtherText>
                      <BodyText weight='500'>{roleData.logisticsInfo.hasFoodAllowance ? 'Yes' : 'No'}</BodyText>
                    </Flex>
                    {roleData.logisticsInfo.hasFoodAllowance && (
                      <>
                        <Flex column gap='0.5rem'>
                          <OtherText color='#888'>Food Allowance (In Company)</OtherText>
                          <BodyText weight='500'>{formatCurrency(roleData.logisticsInfo.foodAllowanceInCompanyAmount)}</BodyText>
                        </Flex>
                        <Flex column gap='0.5rem'>
                          <OtherText color='#888'>Food Allowance (Out Company)</OtherText>
                          <BodyText weight='500'>{formatCurrency(roleData.logisticsInfo.foodAllowanceOutCompanyAmount)}</BodyText>
                        </Flex>
                      </>
                    )}
                  </Flex>
                </Flex>

                <Flex column gap='1rem'>
                  <TitleText weight='600' style={{ fontSize: '14px' }}>Accommodation</TitleText>
                  <Flex column gap='0.75rem' style={{ marginLeft: '1rem' }}>
                    <Flex column gap='0.5rem'>
                      <OtherText color='#888'>Accommodation Provided</OtherText>
                      <BodyText weight='500'>{roleData.logisticsInfo.hasAccommodationProvided ? 'Yes' : 'No'}</BodyText>
                    </Flex>
                    <Flex column gap='0.5rem'>
                      <OtherText color='#888'>Accommodation Allowance</OtherText>
                      <BodyText weight='500'>{roleData.logisticsInfo.hasAccommodationAllowance ? 'Yes' : 'No'}</BodyText>
                    </Flex>
                    {roleData.logisticsInfo.hasAccommodationAllowance && (
                      <>
                        <Flex column gap='0.5rem'>
                          <OtherText color='#888'>Accommodation Allowance (In Company)</OtherText>
                          <BodyText weight='500'>{formatCurrency(roleData.logisticsInfo.accomodationAllowanceInCompanyAmount)}</BodyText>
                        </Flex>
                        <Flex column gap='0.5rem'>
                          <OtherText color='#888'>Accommodation Allowance (Out Company)</OtherText>
                          <BodyText weight='500'>{formatCurrency(roleData.logisticsInfo.accomodationAllowanceOutCompanyAmount)}</BodyText>
                        </Flex>
                      </>
                    )}
                  </Flex>
                </Flex>

                {/* Transportation */}
                <Flex column gap='1rem'>
                  <TitleText weight='600' style={{ fontSize: '14px' }}>Transportation</TitleText>
                  <Flex column gap='0.75rem' style={{ marginLeft: '1rem' }}>
                    <Flex column gap='0.5rem'>
                      <OtherText color='#888'>Transportation Type</OtherText>
                      <BodyText weight='500'>{transportationTypeLabel || 'N/A'}</BodyText>
                    </Flex>
                    <Flex column gap='0.5rem'>
                      <OtherText color='#888'>Transportation Allowance</OtherText>
                      <BodyText weight='500'>{roleData.logisticsInfo.hasTransportationAllowance ? 'Yes' : 'No'}</BodyText>
                    </Flex>
                    {roleData.logisticsInfo.hasTransportationAllowance && (
                      <>
                        <Flex column gap='0.5rem'>
                          <OtherText color='#888'>Transportation Allowance (In Company)</OtherText>
                          <BodyText weight='500'>{formatCurrency(roleData.logisticsInfo.transportationAllowanceInCompanyAmount)}</BodyText>
                        </Flex>
                        <Flex column gap='0.5rem'>
                          <OtherText color='#888'>Transportation Allowance (Out Company)</OtherText>
                          <BodyText weight='500'>{formatCurrency(roleData.logisticsInfo.transportationAllowanceOutCompanyAmount)}</BodyText>
                        </Flex>
                      </>
                    )}
                  </Flex>
                </Flex>

                <Flex column gap='1rem'>
                  <TitleText weight='600' style={{ fontSize: '14px' }}>Responsibility Allowance</TitleText>
                  <Flex column gap='0.75rem' style={{ marginLeft: '1rem' }}>
                    <Flex column gap='0.5rem'>
                      <OtherText color='#888'>Has Responsibility Allowance</OtherText>
                      <BodyText weight='500'>{roleData.logisticsInfo.hasResponsibilityAllowance ? 'Yes' : 'No'}</BodyText>
                    </Flex>
                    {roleData.logisticsInfo.hasResponsibilityAllowance && (
                      <>
                        <Flex column gap='0.5rem'>
                          <OtherText color='#888'>Responsibility Allowance (In Company)</OtherText>
                          <BodyText weight='500'>{formatCurrency(roleData.logisticsInfo.responsibilityAllowanceInCompanyAmount)}</BodyText>
                        </Flex>
                        <Flex column gap='0.5rem'>
                          <OtherText color='#888'>Responsibility Allowance (Out Company)</OtherText>
                          <BodyText weight='500'>{formatCurrency(roleData.logisticsInfo.responsibilityAllowanceOutCompanyAmount)}</BodyText>
                        </Flex>
                      </>
                    )}
                  </Flex>
                </Flex>

                <Flex column gap='1rem'>
                  <TitleText weight='600' style={{ fontSize: '14px' }}>Extra Duty Allowance</TitleText>
                  <Flex column gap='0.75rem' style={{ marginLeft: '1rem' }}>
                    <Flex column gap='0.5rem'>
                      <OtherText color='#888'>Has Extra Duty Allowance</OtherText>
                      <BodyText weight='500'>{roleData.logisticsInfo.hasExtraDutyAllowance ? 'Yes' : 'No'}</BodyText>
                    </Flex>
                    {roleData.logisticsInfo.hasExtraDutyAllowance && (
                      <>
                        <Flex column gap='0.5rem'>
                          <OtherText color='#888'>Extra Duty Allowance (In Company)</OtherText>
                          <BodyText weight='500'>{formatCurrency(roleData.logisticsInfo.extraDutyAllowanceInCompanyAmount)}</BodyText>
                        </Flex>
                        <Flex column gap='0.5rem'>
                          <OtherText color='#888'>Extra Duty Allowance (Out Company)</OtherText>
                          <BodyText weight='500'>{formatCurrency(roleData.logisticsInfo.extraDutyAllowanceOutCompanyAmount)}</BodyText>
                        </Flex>
                      </>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Box>
          )}
        </Flex>
      </PageWrapper>
    </>
  )
}
